import '@/utils/temporal-polyfill';
// oxlint-disable-next-line no-restricted-imports -- `enMessages` has no sub-export yet
import DashingUI, { enMessages } from '@dashhudson/dashing-ui';
import { applyStoredColorScheme } from '@dashhudson/dashing-ui/color-scheme-boot';
import { datadogLogs } from '@datadog/browser-logs';
import { createGtm } from '@gtm-support/vue-gtm';
import { PiniaColada } from '@pinia/colada';
import { PiniaColadaAutoRefetch } from '@pinia/colada-plugin-auto-refetch';
import { PiniaColadaRetry } from '@pinia/colada-plugin-retry';
import { createHead } from '@unhead/vue/client';
import mixpanel from 'mixpanel-browser';
import { createApp } from 'vue';

import { isCancelError } from '@/apis/abort';
import { initApiClient } from '@/apis/api-client';
import { getHttpErrorStatus } from '@/apis/http-errors';
import DragMove from '@/directives/drag-move';
import DragSelect from '@/directives/drag-select';
import InfiniteScroll from '@/directives/infinite-scroll';
import LineClampDirective from '@/directives/line-clamp';
import SlideSelect from '@/directives/slide-select';
import { env } from '@/env';
import { i18n } from '@/i18n';
import { pseudoLocale, pseudoMessages } from '@/i18n/pseudo';
import AdobeExpressEmbedPlugin from '@/plugins/adobe-express-embed';
import DashingUIOverridesPlugin from '@/plugins/dashing-ui-overrides';
import FloatingVuePlugin from '@/plugins/floating-vue';
import SanitizeHtmlPlugin from '@/plugins/sanitize-html';
import RootApp from '@/RootApp.vue';
import router, { installRouterLifecycle } from '@/router-lifecycle';
import { PiniaColadaClearOnUserChange } from '@/shared/queries/clear-on-user-change-plugin';
import { PiniaColadaRefreshAbortedPending } from '@/shared/queries/refresh-aborted-pending-plugin';
import { useIdentityStore } from '@/stores/identity';
import pinia from '@/stores/pinia';
import setupDayJsPlugins from '@/utils/dayjsExtensions';
import { getDateComponentTimezone } from '@/utils/timezone';

// First, as at its old import-time call: the stores it creates predate `app.use(pinia)`, so they
// still skip pinia plugins (resetStore), and its hooks must exist before the initial navigation.
installRouterLifecycle();
setupDayJsPlugins();

// Pre-paint, so a reload into the dark preview doesn't flash light while `getSelf` resolves.
applyStoredColorScheme();

const app = createApp(RootApp);
app.use(pinia);
app.use(i18n);
initApiClient();
app.use(PiniaColada, {
  plugins: [
    PiniaColadaRetry({
      retry: (failureCount, error) => {
        if (isCancelError(error)) return false;
        const status = getHttpErrorStatus(error);
        if (status) {
          // Retry 429 (rate limit) and 5xx (server errors)
          if (status === 429 || status >= 500) {
            return failureCount < 3;
          }
          // Don't retry other 4xx client errors
          return false;
        }
        // Retry network errors (no response)
        return failureCount < 3;
      },
      delay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
    }),
    // Opt-in per query via `autoRefetch: <ms>` (or a function/boolean).
    // Default off — queries don't poll unless they ask to.
    PiniaColadaAutoRefetch(),
    PiniaColadaRefreshAbortedPending(),
    PiniaColadaClearOnUserChange(() => useIdentityStore(pinia).identity?.id),
  ],
  queryOptions: {
    // Data is fresh for 5 minutes (won't refetch if navigating back quickly)
    staleTime: 5 * 60 * 1000,
    // Keep inactive query data for 30 minutes before garbage collection
    // This ensures data survives component unmount during navigation
    gcTime: 30 * 60 * 1000,
    // Refetch when data is stale and component mounts
    refetchOnMount: true,
    // Don't refetch just because window regained focus
    refetchOnWindowFocus: false,
    // Don't refetch on reconnect
    refetchOnReconnect: false,
  },
});
app.use(router);
app.use(FloatingVuePlugin);
app.use(SanitizeHtmlPlugin);
app.use(AdobeExpressEmbedPlugin);
app.use(DashingUI, {
  // Getter, not a value — identity and flags both load after mount, so dashing-ui must re-read it.
  timezone: getDateComponentTimezone,
  imageApi: {
    prodUrl: import.meta.env.VITE_PROD_IMAGE_API_URL || 'https://images.dashsocial.com',
    devUrl: import.meta.env.VITE_DEV_IMAGE_API_URL || 'https://images.dhdev.co',
    oldDevUrl: import.meta.env.VITE_OLD_DEV_IMAGE_API_URL || 'https://images.dhdev.io',
  },
  datadog: datadogLogs,
  mixpanel,
  globalContext: {
    app: 'dash-web',
    version: env.revision,
  },
  logger: {
    alwaysUseConsole: !!env.isLocalDev,
    consolePrefix: '',
  },
  messages: pseudoLocale ? pseudoMessages(enMessages) : undefined,
});
app.use(DashingUIOverridesPlugin);
app.directive('drag-move', DragMove);
app.directive('drag-select', DragSelect);
app.directive('slide-select', SlideSelect);
app.directive('line-clamp', LineClampDirective);
app.directive('infinite-scroll', InfiniteScroll);

app.use(
  createGtm({
    id: env.gtmId,
    vueRouter: router,
    enabled: env.gtmEnabled,
  }),
);

const head = createHead();
app.use(head);

router.isReady().then(() => app.mount('#app'));
